import {getCurrentUserIdToken} from "../../utils/user";
import intl from "react-intl-universal";

import {UserRole, UserSessionState} from "@stores/UserSessionState";
import {RootStoreModel} from "@stores/RootStore";

import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {UserCard} from "../../ui/components/UserCard";

import {snackBarRef} from "@stores/snackbarRef";
import {MODALS} from "../../ui/screens/modals/modal-names";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import { AuthServices } from "../../platform/services/types/ServiceDependencies";
import {AppState} from "@stores/AppState";

export interface UpdateRoleParams {
    action: 'changeRole';
    targetSub: string;
    targetRole: UserRole;
}

export interface DeleteUserParams {
    action: 'deleteUser';
    targetSub: string;
}


export type TargetData = UpdateRoleParams | DeleteUserParams;


export const makeAuthenticatedRequest = async (
    services: AuthServices,
    targetData: TargetData, 
    successMessage: string
): Promise<any> => {
    const enqueueSnackbar = snackBarRef.current!.enqueueSnackbar;
    try {
        const response = await fetch(
            import.meta.env.VITE_APP_ACCESS_GATEWAY_URL,
            {
                method: 'POST', // or 'POST', 'PUT', etc., depending on your API
                body: JSON.stringify(targetData),
                headers: {
                    Authorization: (await getCurrentUserIdToken(services.authService))!,
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                enqueueSnackbar({
                    message: intl.get('user-role.auth_error').d(`You are not authorized to perform this action`),
                    variant: 'error',
                });
                return
            } else {
                enqueueSnackbar({
                    message: intl.get('user-role.general_error').d(`Something went wrong, but we're actively working to fix this issue. In the meantime, please feel free to contact us.`),
                    variant: 'error',
                });
                logErrorToSentry(new Error(`HTTP error! Status: ${response.status}  Message: ${await response.text()}`));
            }
        }

        const json = await response.json();
        enqueueSnackbar({
            message: successMessage,
            variant: 'success',
        });
        return json;
    } catch (error: unknown) {
        enqueueSnackbar({
            message: intl.get('user-role.general_error').d(`Something went wrong, but we're actively working to fix this issue. In the meantime, please feel free to contact us.`),
            variant: 'error',
        });
        logErrorToSentry(error as Error);
    }
}

export interface ChangeUserRoleParams {
    targetSub: string,
    targetRole: UserRole,
}

export const changeUserRole = async (
    services: AuthServices,
    {
        targetSub,
        targetRole,
    }: ChangeUserRoleParams
) => {
    return await makeAuthenticatedRequest(
        services,
        {
            action: 'changeRole',
            targetSub: targetSub,
            targetRole: targetRole,
        },
        intl.get('user-role.change_success').d('Successfully changed role')
    )
}

export const deleteUser = async (services: AuthServices, targetSub: string) => {
    return await makeAuthenticatedRequest(
        services,
        {
            action: 'deleteUser',
            targetSub: targetSub,
        },
        intl.get('user-role.delete_success').d('Successfully deleted user')
    )
}

export const safeDeleteUser = (
    services: AuthServices,
    appState: AppState,
    targetSub: string
) => {
    const user = appState.userState!.usersDS.getUserBySub(targetSub)!;
    const isMobile = screenSizeProvider.view !== 'desktop';
    appState.setModal({
        name: MODALS.USER_DELETE, params: {
            confirmWarning: (
                <>
                    {intl.get('confirm.delete_user.warning.top.text').d("Are you sure you want to remove this member:")}
                    <UserCard
                        user={user}
                        size={36}
                        isEmail={true}
                        isInline={!isMobile}
                        className={"DeleteModal__userCard"}
                        emailWithBrackets={true}
                    />
                    {intl.getHTML('confirm.delete_user.warning.bottom.text.start').d(`<b>This action cannot be undone.</b> This will remove the user’s account and all associated data, except for contributions to the knowledge base that will remain intact.`)}
                </>
            ),
            onConfirm: async () => {
                await deleteUser(services, targetSub);
                appState.setModal(null);
            },
        }
    });
}


